import React from 'react';

import config from 'config';
import PropTypes from 'prop-types';
import styled from 'styled-components';

// Styled Grid Components

import CarouselContext from '~/shared/components/CarouselContext';
import { useMembership } from '~/techstyle-shared/react-accounts';
import {
  Button as BaseButton,
  LoadingStatus,
  useBreakpoint,
  mobile,
  PlaceholderGrid,
} from '~/techstyle-shared/react-components';

import { BreakpointsShape } from '../../constants/customPropTypes';
import GridScrollContext from '../../context/GridScrollContext';
import StyleGuideContext from '../../context/StyleGuideContext';
import ProductBrowser, {
  individualSortOptionPropType,
} from '../ProductBrowser';
import ProductGridSEO from '../ProductGridSEO';
import StyleGuide from '../StyleGuide';
import StyleGuideFilterPills from '../StyleGuideFilterPills/StyleGuideFilterPills';

const GridCtaButton = styled(BaseButton).attrs({ variant: 'unstyled' })`
  ${({ theme }) => theme.gridStyles.gridCtaButtonStyle};
  font-family: ${({ theme: { fontStacks }, $fontVariant }) =>
    fontStacks[$fontVariant] ?? fontStacks.baseFamily};
`;

const InitialLoadFableticsPlaceholderGrid = styled(PlaceholderGrid)`
  --chinHeight: 114px;
  margin-bottom: var(--rowGap);
  max-width: 75%;
  left: 50%;
  transform: translateX(-50%);
  margin-top: 100px;
`;

const CtaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: ${({ theme: { sizes } }) => sizes.md}px;
`;

const ProductBrowserWrapper = styled.div`
  max-width: ${({ theme: { site }, $isCarouselGrid }) =>
    $isCarouselGrid ? '1280' : site.maxWidth}px;
  margin: 0 auto;

  ${({ $hideGridContent }) =>
    $hideGridContent &&
    `
		width: 268px;
		margin-right: ${({ theme: { sizes } }) => sizes.lg}px;
	`}

  font-family: ${({ fontVariant, theme: { fontStacks } }) =>
    fontStacks[fontVariant] ?? fontStacks.baseFamily};
`;

const ProductBrowserBody = styled(ProductBrowser.Body)`
  ${({ $isCarouselGrid }) =>
    $isCarouselGrid &&
    `
      padding: 0 0px 32px 16px;
    `}
  ${mobile`
    ${({ $isCarouselGrid }) =>
      $isCarouselGrid &&
      `
      padding: 0 0px 32px 12px;
    `}
  `}

  ${({ $hideGridContent }) =>
    $hideGridContent &&
    `
		padding: unset;
	`}
`;

const NodeWrapper = styled.div`
  ${({ $hideSideBar, $hideGridContent }) =>
    !$hideSideBar &&
    $hideGridContent &&
    `
		z-index: 3;
	`}
`;

const MobileGridHeader = styled.h1`
  padding: 16px 16px 0 16px;

  ${({ $isCarouselGrid, theme }) =>
    !$isCarouselGrid && theme.mobileGridHeaderStyle}

  ${({ $isCarouselGrid, theme }) =>
    $isCarouselGrid && theme.gridStyles.gridCarouselLabelStyle}
  ${({ $isCarouselGrid }) => $isCarouselGrid && `padding-left: 0;`}
`;

const CAROUSEL_GRID_COLUMNS_SETTINGS = {
  tablet: 1,
  mobile: 1,
};

export default function GridNode({
  activeCategory,
  autoAppliedInitialFilters,
  baseFilters,
  breadcrumbs = [],
  breakpoints,
  hideGridContent = false,
  hideHeader,
  hideSideBar,
  hideSortOption = false,
  gridCTA = {},
  imageType,
  index = -1,
  initialProducts,
  initialFilters,
  initialSortOption,
  showLastChance = false,
  isCarouselGrid = false,
  isCollectionGrid = false,
  isTabGrid = false,
  keyId,
  label,
  fontVariant,
  maxItemCount = null,
  productSource,
  primaryFplId,
  profileSizes,
  gridSlotsAsset,
  gridStyleGuide,
  shouldShowCategoryHeading,
  shouldShowCategoryDropdown,
  shouldShowColorsLink,
  shouldShowGridReviews,
  shouldUseSharedFilters,
  sortOptions,
  gender = 'F',
  pdpLinkOverride,
  isGqlProducts = false,
  ...rest
}) {
  const { isMobile } = useBreakpoint();
  const { isLead } = useMembership();
  const genderIndicator = gender && (gender === 'F' ? 'Womens' : 'Mens');

  // We want to be able to specify whether a grid is a carousel for mobile and desktop indepdenedntly. This means changing
  //  its structure from a boolean to an object. The following ensures backwards compatibility. It maintains support of
  //  isCarousel as a boolean as well as the new way (isMobile and isDesktop) definition as an object. The boolean check
  //  can be removed once the old way is fully deprecated.
  if (typeof isCarouselGrid !== 'boolean') {
    isCarouselGrid = isMobile ? isCarouselGrid.mobile : isCarouselGrid.desktop;
  }

  // Do not show filters if we are looking at a carousel
  const isFiltersDisabled = isCarouselGrid;

  if (!maxItemCount) {
    maxItemCount = index !== -1 ? 10 : null;

    if (isCarouselGrid) {
      maxItemCount = 16;
    }
  }

  const hasGridCTA = isMobile ? gridCTA.mobile : gridCTA.desktop;
  const isDesktopCarousel = isCarouselGrid && !isMobile;

  const ProductBrowserContextWrapper = isCarouselGrid
    ? CarouselContext
    : React.Fragment;

  const gridColumnsSettings = isCarouselGrid
    ? CAROUSEL_GRID_COLUMNS_SETTINGS
    : undefined;

  const hasStyleGuide =
    gridStyleGuide?.shouldShowStyleGuide &&
    !!gridStyleGuide?.assetContainers?.desktop &&
    !!gridStyleGuide?.assetContainers?.mobile &&
    !!gridStyleGuide?.tagList;

  // placeholder settings
  const placeholderPropsGrid = isMobile
    ? config.get('public.brand.gridColumnSettings').mobile
    : config.get('public.brand.gridColumnSettings').desktop;

  const placeholderPropsCarousel = isMobile
    ? config.get('public.brand.carouselPlaceholderColumnSettings').mobile
    : config.get('public.brand.carouselPlaceholderColumnSettings').desktop;

  const placeholderProps = isCarouselGrid
    ? placeholderPropsCarousel
    : placeholderPropsGrid;

  hideSideBar = hideSideBar || isCarouselGrid;

  return (
    <NodeWrapper
      $hideSideBar={hideSideBar}
      $hideGridContent={hideGridContent}
      {...rest}
    >
      <GridScrollContext>
        <StyleGuideContext tagList={gridStyleGuide?.tagList}>
          <ProductBrowserWrapper
            $index={index}
            $lead={isLead}
            $mobile={isMobile}
            $hideSideBar={hideSideBar}
            $hideGridContent={hideGridContent}
            $isCarouselGrid={isCarouselGrid}
            data-cnstrc-browse
            data-cnstrc-num-results={initialProducts?.total}
          >
            <LoadingStatus>
              <ProductBrowser.LoadingGridsSpinner />
              <ProductBrowserContextWrapper>
                {initialProducts ? (
                  <ProductBrowser
                    activeCategory={activeCategory}
                    autoAppliedFilters={autoAppliedInitialFilters}
                    baseFilters={baseFilters}
                    breadcrumbs={breadcrumbs}
                    initialFilters={initialFilters}
                    aggregations={initialProducts?.aggregations}
                    // filterSettings={filterSettings}
                    initialSortOption={initialSortOption}
                    profileSize={profileSizes}
                    sortOptions={sortOptions}
                    // In ProductBrowser, we don't currently correctly reset the
                    // `productsReducer` state when the component is re-rendered due
                    // to a SPA navigation. So even though we'd want its products
                    // and `payloadKey` to be re-initialized with the new content
                    // from `getInitialProps`, it instead keeps the existing state
                    // mounted and thinks the `payloadKey` changed, thus triggering
                    // a duplicate request for the first page of products (which
                    // already happened in `getInitialProps`). We can fix this, but
                    // it will take some work on that component's state lifecycle
                    // and maybe adding some refs... for now we instead remount the
                    // entire ProductBrowser when the route changes (as determined
                    // by `getInitialProps` being called, which will increment the
                    // `productBrowserKey`). You might think we can instead use,
                    // say, `routeInfo.canonicalPath`, or the serialized
                    // `initialFilters` or some prop we already have anyway, but
                    // it's possible for the user to re-navigate to the grid page
                    // they're already on, so those wouldn't necessarily change.
                    key={keyId}
                    shouldUseSharedFilters={shouldUseSharedFilters}
                    enableQueryParams={!isTabGrid}
                  >
                    {hasStyleGuide && (
                      <StyleGuide
                        containerName={
                          isMobile
                            ? gridStyleGuide.assetContainers.mobile
                            : gridStyleGuide.assetContainers.desktop
                        }
                        leadAsset={gridStyleGuide.assetContainers.marketingCard}
                      />
                    )}

                    {isMobile &&
                      shouldShowCategoryDropdown &&
                      !isCarouselGrid && <ProductBrowser.MobileNavigation />}

                    {isMobile && label && (
                      <MobileGridHeader $isCarouselGrid={isCarouselGrid}>
                        {label}
                      </MobileGridHeader>
                    )}

                    {hasStyleGuide && isMobile && <StyleGuideFilterPills />}

                    {!hideHeader && (
                      <ProductBrowser.Header
                        label={label}
                        showCategoryFields={shouldShowCategoryDropdown}
                        shouldUseSharedFilters={shouldUseSharedFilters}
                        shouldShowCategoryHeading={shouldShowCategoryHeading}
                        gender={genderIndicator}
                        isCarouselGrid={isCarouselGrid}
                        isCollectionGrid={isCollectionGrid}
                        disabledFilters={isFiltersDisabled}
                        hasStyleGuide={hasStyleGuide}
                        hideSortOption={hideSortOption}
                        gridCTA={gridCTA}
                        hideSideBar={hideSideBar}
                        isTabGrid={isTabGrid}
                      />
                    )}
                    <ProductBrowserBody
                      $hideGridContent={hideGridContent}
                      $isCarouselGrid={isCarouselGrid}
                      gridColumnsSettings={gridColumnsSettings}
                    >
                      {!hideSideBar && (
                        <ProductBrowser.SideBar
                          showCategoryFields={shouldShowCategoryDropdown}
                          shouldUseSharedFilters={shouldUseSharedFilters}
                          shouldShowCategoryHeading={shouldShowCategoryHeading}
                        />
                      )}
                      {isMobile && !shouldUseSharedFilters && (
                        <ProductBrowser.FilterDrawer />
                      )}
                      {!hideGridContent && (
                        <ProductBrowser.GridContent>
                          <ProductBrowser.ProductGrid
                            breakpoints={breakpoints}
                            fplId={primaryFplId}
                            gridSlotsAsset={gridSlotsAsset}
                            imageType={imageType}
                            index={index}
                            initialProducts={initialProducts}
                            initialSortOption={initialSortOption}
                            showLastChance={showLastChance}
                            isCarouselGrid={isCarouselGrid}
                            productSource={productSource}
                            maxItemCount={maxItemCount}
                            showColorsLink={shouldShowColorsLink}
                            showGridReviews={shouldShowGridReviews}
                            pdpLinkOverride={pdpLinkOverride}
                            isGqlProducts={isGqlProducts}
                          />
                          {hasGridCTA && !isDesktopCarousel && (
                            <CtaContainer>
                              <GridCtaButton
                                href={gridCTA.url}
                                $fontVariant={gridCTA.fontVariant}
                              >
                                {gridCTA.label}
                              </GridCtaButton>
                            </CtaContainer>
                          )}
                        </ProductBrowser.GridContent>
                      )}
                    </ProductBrowserBody>
                  </ProductBrowser>
                ) : (
                  <>
                    <InitialLoadFableticsPlaceholderGrid
                      imageAspectRatio={1}
                      data-autotag="empty-grid-message"
                      {...placeholderProps}
                    />
                  </>
                )}
              </ProductBrowserContextWrapper>
            </LoadingStatus>
          </ProductBrowserWrapper>
        </StyleGuideContext>
      </GridScrollContext>
      {label?.defaultMessage && (
        <ProductGridSEO categoryLabel={label.defaultMessage} />
      )}
    </NodeWrapper>
  );
}

GridNode.propTypes = {
  activeCategory: PropTypes.object,
  autoAppliedInitialFilters: PropTypes.object,
  baseFilters: PropTypes.object,
  breadcrumbs: PropTypes.array,
  breakpoints: BreakpointsShape,
  cta: PropTypes.node,
  fontVariant: PropTypes.string,
  gender: PropTypes.string,
  gridCTA: PropTypes.shape({
    desktop: PropTypes.bool,
    mobile: PropTypes.bool,
    label: PropTypes.oneOfType([
      PropTypes.shape({
        key: PropTypes.string,
        defaultMessage: PropTypes.string,
      }),
      PropTypes.string,
    ]),
    url: PropTypes.string,
    fontVariant: PropTypes.string,
  }),
  gridSlotsAsset: PropTypes.array,
  gridStyleGuide: PropTypes.object,
  hideGridContent: PropTypes.bool,
  hideHeader: PropTypes.bool,
  hideSideBar: PropTypes.bool,
  hideSortOption: PropTypes.bool,
  imageType: PropTypes.string,
  index: PropTypes.number,
  initialFilters: PropTypes.object,
  initialProducts: PropTypes.object,
  initialSortOption: individualSortOptionPropType,
  isCarouselGrid: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  isCollectionGrid: PropTypes.bool,
  isGqlProducts: PropTypes.bool,
  isTabGrid: PropTypes.bool,
  keyId: PropTypes.number,
  label: PropTypes.oneOfType([PropTypes.object, PropTypes.string]),
  maxItemCount: PropTypes.number,
  pdpLinkOverride: PropTypes.string,
  primaryFplId: PropTypes.number,
  productSource: PropTypes.string,
  profileSizes: PropTypes.object,
  shouldShowCategoryDropdown: PropTypes.bool,
  shouldShowCategoryHeading: PropTypes.bool,
  shouldShowColorsLink: PropTypes.bool,
  shouldShowGridReviews: PropTypes.bool,
  shouldUseSharedFilters: PropTypes.bool,
  showLastChance: PropTypes.bool,
  sortOptions: PropTypes.arrayOf(individualSortOptionPropType),
};
